/**
 * Production prompt: a single "what would you say here?" field. One exchange,
 * no follow-up. Graded by the same path as a conversation turn (docs/01).
 */

import { api, ApiError } from "../api.ts";
import type { AttemptRecord, ProductionPayload, TurnResponse } from "../types.ts";
import { el, gloss, primaryButton, ptSpan } from "../ui.ts";
import { highlightedCorrection } from "./conversation.ts";
import { uuid, type BlockContext, type RenderedBlock } from "./types.ts";

export function renderProduction(context: BlockContext): RenderedBlock {
	const payload = context.block.payload as unknown as ProductionPayload;
	const shownAt = Date.now();
	const attemptId = uuid();
	let submitted = false;

	const body: Node[] = [];
	body.push(el("div", { class: "meta", text: "What would you say?" }));
	body.push(el("p", { class: "pt-lg", style: "margin-top:10px", lang: "en", text: payload.prompt_en }));

	if (payload.hint_pt.trim() !== "") {
		body.push(
			el(
				"p",
				{ class: "en", style: "margin-top:10px" },
				"Try using ",
				ptSpan(payload.hint_pt, "pt"),
			),
		);
	}

	const input = el("textarea", {
		lang: "pt-BR",
		rows: "3",
		placeholder: "Escreva em português…",
		"aria-label": "Your answer in Portuguese",
	});
	body.push(el("div", { class: "composer" }, input));

	const result = el("div", { class: "stack--tight", "aria-live": "polite" });
	body.push(result);

	const attempts: AttemptRecord[] = [];

	const check = async (): Promise<void> => {
		if (submitted) return;
		const text = input.value.trim();
		if (text === "") return;
		submitted = true;
		input.disabled = true;

		if (context.isTest) {
			context.finish({ attempts: [], deferred: { id: attemptId, block_id: context.block.id, text } });
			return;
		}

		context.setFooter(primaryButton("Checking…", () => {}, { disabled: true }));

		try {
			const response = await api.turn(context.session.id, {
				block_id: context.block.id,
				text,
				round: 1,
				history: [],
			});
			renderFeedback(response, text);
			attempts.push(buildAttempt(response.assessment === "correct", text));
		} catch (error) {
			const message = error instanceof ApiError ? error.message : "Grading needs a connection.";
			result.replaceChildren(el("div", { class: "notice", text: message }));
			// Offline: keep the answer, let the next online session's planner see
			// it as an unrated production attempt rather than losing it.
			attempts.push(buildAttempt(false, text));
		} finally {
			context.setFooter(primaryButton("Continue", () => context.finish({ attempts })));
		}
	};

	function buildAttempt(correct: boolean, text: string): AttemptRecord {
		return {
			id: attemptId,
			session_id: context.session.id,
			block_id: context.block.id,
			item_id: null,
			modality: "production",
			correct,
			confidence: null,
			response_text: text,
			elapsed_ms: Date.now() - shownAt,
			created_at: Date.now(),
		};
	}

	function renderFeedback(response: TurnResponse, learnerText: string): void {
		result.replaceChildren();
		if (response.assessment === "correct") {
			// The check mark is the congratulation — nothing more (docs/01).
			result.append(
				el("div", { class: "chip chip--jade", text: "✓ Correct" }),
			);
		} else {
			const card = el(
				"div",
				{ class: "correction" },
				el("div", { class: "meta", text: response.assessment === "minor" ? "Almost — one small fix" : "Not quite" }),
				highlightedCorrection(learnerText, response.corrected_pt),
			);
			if (response.explanation_en.trim() !== "") {
				card.append(el("p", { class: "en en--ink", style: "margin-top:7px", lang: "en", text: response.explanation_en }));
			}
			result.append(card);
		}

		if (response.reply_pt.trim() !== "") {
			const reply = el("div", { class: "card", style: "margin-top:12px" }, el("p", { class: "pt", lang: "pt-BR", text: response.reply_pt }));
			const line = gloss(response.reply_en, context.englishRatio);
			if (line !== null) reply.append(line);
			result.append(reply);
		}
	}

	const footer = primaryButton(context.isTest ? "Next question" : "Check answer", () => {
		void check();
	}, context.isTest ? { variant: "ochre" } : {});

	return { body, footer };
}
